import type {
  ColumnFiltersState,
  PaginationState,
  RowSelectionState,
  SortingState,
  VisibilityState,
} from "@tanstack/react-table";
import { useCallback, useState } from "react";
import {
  DEFAULT_COLUMN_FILTERS,
  DEFAULT_COLUMN_VISIBILITY,
  DEFAULT_PAGINATION_STATE,
  DEFAULT_ROW_SELECTION,
  DEFAULT_SORTING_STATE,
} from "../types";

/**
 * Table state configuration
 */
export interface TableStateConfig {
  initialPagination?: PaginationState;
  initialSorting?: SortingState;
  initialColumnFilters?: ColumnFiltersState;
  initialColumnVisibility?: VisibilityState;
  initialRowSelection?: RowSelectionState;
  initialGlobalFilter?: string;
}

/**
 * Return type for useTableState hook
 */
export interface TableState {
  pagination: PaginationState;
  sorting: SortingState;
  columnFilters: ColumnFiltersState;
  columnVisibility: VisibilityState;
  rowSelection: RowSelectionState;
  globalFilter: string;
  setPagination: React.Dispatch<React.SetStateAction<PaginationState>>;
  setSorting: React.Dispatch<React.SetStateAction<SortingState>>;
  setColumnFilters: React.Dispatch<React.SetStateAction<ColumnFiltersState>>;
  setColumnVisibility: React.Dispatch<React.SetStateAction<VisibilityState>>;
  setRowSelection: React.Dispatch<React.SetStateAction<RowSelectionState>>;
  setGlobalFilter: React.Dispatch<React.SetStateAction<string>>;
  resetState: () => void;
}

/**
 * Hook to manage table state
 * Provides all state and setters for TanStack Table
 */
export function useTableState(config: TableStateConfig = {}): TableState {
  const [pagination, setPagination] = useState<PaginationState>(
    config.initialPagination ?? DEFAULT_PAGINATION_STATE,
  );
  const [sorting, setSorting] = useState<SortingState>(
    config.initialSorting ?? DEFAULT_SORTING_STATE,
  );
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>(
    config.initialColumnFilters ?? DEFAULT_COLUMN_FILTERS,
  );
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(
    config.initialColumnVisibility ?? DEFAULT_COLUMN_VISIBILITY,
  );
  const [rowSelection, setRowSelection] = useState<RowSelectionState>(
    config.initialRowSelection ?? DEFAULT_ROW_SELECTION,
  );
  const [globalFilter, setGlobalFilter] = useState<string>(
    config.initialGlobalFilter ?? "",
  );

  const resetState = useCallback(() => {
    setPagination(config.initialPagination ?? DEFAULT_PAGINATION_STATE);
    setSorting(config.initialSorting ?? DEFAULT_SORTING_STATE);
    setColumnFilters(config.initialColumnFilters ?? DEFAULT_COLUMN_FILTERS);
    setColumnVisibility(
      config.initialColumnVisibility ?? DEFAULT_COLUMN_VISIBILITY,
    );
    setRowSelection(config.initialRowSelection ?? DEFAULT_ROW_SELECTION);
    setGlobalFilter(config.initialGlobalFilter ?? "");
  }, [config]);

  return {
    pagination,
    sorting,
    columnFilters,
    columnVisibility,
    rowSelection,
    globalFilter,
    setPagination,
    setSorting,
    setColumnFilters,
    setColumnVisibility,
    setRowSelection,
    setGlobalFilter,
    resetState,
  };
}
